/**
 * creditoClienteService.ts
 * Servicio exclusivo para el portal de clientes de crédito.
 * Usa un token separado (credito_cliente_token) para no interferir con el auth del staff.
 */

import { buildApiUrl } from '../../../services/apiConfig';

export const CC_TOKEN_KEY = 'credito_cliente_token';
export const CC_USER_KEY = 'credito_cliente_user';

const API_TIMEOUT = 10000;

export interface CCCliente {
  id: string;
  nombre: string;
  local_id: string;
  local_nombre?: string;
  banca_id: string;
  saldo_pendiente: string;
  limite_credito: string;
  disponible: string | null;
  sin_limite: boolean;
}

export interface CCResumen {
  saldo_pendiente: string;
  limite_credito: string;
  disponible: string | null;
  sin_limite: boolean;
  limite_local: string;
  puede_registrar_abonos: boolean;
}

export interface CCSorteo {
  id: string;
  nombre: string;
  tipo_id: string;
  estado: string;
  hora_cierre: string | null;
  fecha: string | null;
}

export interface CCJugada {
  id: string;
  num1: string;
  num2: string | null;
  monto: string;
  estado: string;
}

export interface CCTicket {
  id: string;
  ticket_number: string | null;
  sorteo_id: string;
  sorteo_nombre?: string | null;
  sorteo_tipo_id?: string | null;
  sorteo_estado?: string | null;
  sorteo_hora_cierre?: string | null;
  local_id?: string | null;
  vendedor_id?: string | null;
  total: string;
  estado: string;
  metodo_pago: string;
  created_at: string | null;
  fecha: string | null;
  jugadas: CCJugada[];
}

export interface CCMovimiento {
  id: string;
  tipo: string;
  monto: string;
  efecto: string;
  descripcion: string | null;
  saldo_anterior: string | null;
  saldo_nuevo: string | null;
  referencia_tipo: string | null;
  referencia_id: string | null;
  created_at: string | null;
}

export interface CCJugadaIn {
  num1: string;
  num2?: string | null;
  monto: number;
}

function getCCToken(): string | null {
  return localStorage.getItem(CC_TOKEN_KEY);
}

async function fetchCC<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  const token = getCCToken();

  try {
    const response = await fetch(buildApiUrl(`/credito-cliente${endpoint}`), {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    clearTimeout(timeoutId);

    if (response.status === 401 || response.status === 403) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || 'Sesión expirada');
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Error ${response.status}`);
    }
    return response.json();
  } catch (e: any) {
    clearTimeout(timeoutId);
    if (e.name === 'AbortError') throw new Error('Conexión lenta. Intente nuevamente.');
    throw e;
  }
}

export const creditoClienteService = {
  async login(username: string, password: string): Promise<{ token: string; cliente: CCCliente }> {
    const res = await fetch(buildApiUrl('/credito-cliente/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Credenciales inválidas');
    }
    const data = await res.json();
    localStorage.setItem(CC_TOKEN_KEY, data.access_token);
    localStorage.setItem(CC_USER_KEY, JSON.stringify(data.cliente));
    return { token: data.access_token, cliente: data.cliente };
  },

  logout() {
    localStorage.removeItem(CC_TOKEN_KEY);
    localStorage.removeItem(CC_USER_KEY);
  },

  getStoredUser(): CCCliente | null {
    try {
      const raw = localStorage.getItem(CC_USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  isLoggedIn(): boolean {
    return Boolean(getCCToken());
  },

  async me(): Promise<CCCliente> {
    return fetchCC<CCCliente>('/me');
  },

  async resumen(): Promise<CCResumen> {
    return fetchCC<CCResumen>('/resumen');
  },

  async sorteosActivos(): Promise<CCSorteo[]> {
    return fetchCC<CCSorteo[]>('/sorteos-activos');
  },

  async crearTicket(sorteo_id: string, jugadas: CCJugadaIn[]): Promise<CCTicket> {
    return fetchCC<CCTicket>('/tickets', {
      method: 'POST',
      body: JSON.stringify({ sorteo_id, jugadas }),
    });
  },

  async misTickets(): Promise<CCTicket[]> {
    return fetchCC<CCTicket[]>('/tickets/activos');
  },

  async detalleTicket(ticketId: string): Promise<CCTicket> {
    return fetchCC<CCTicket>(`/tickets/${ticketId}`);
  },

  async misMovimientos(): Promise<CCMovimiento[]> {
    return fetchCC<CCMovimiento[]>('/movimientos');
  },

  async anularTicket(ticketId: string): Promise<CCTicket> {
    return fetchCC<CCTicket>(`/tickets/${ticketId}/anular`, { method: 'POST' });
  },
};
